import ToggleGroup from "@/v2-ui/toggleGroup"
import {
  COMPOSE_VIEW_LABELS,
  type ComposeView,
} from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.compose.constants"
import { capabilityGrantOf } from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.capability.utils"
import {
  individualPicksOf,
  useCatalogPlacement,
} from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.compose.catalog.hooks"
import type {
  FeatureGroupPickedLike,
  FeatureGroupRuleLike,
} from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.compose.utils"
import { grantOf } from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.content.utils"
import { useFeatureGroupLens } from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.lens.context"
import { reachesThroughLens } from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.lens.utils"
import { useFeatureContentCatalogQuery } from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.contentCatalog.query"
import { useFeatureSurfaceCatalogQuery } from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.surfaceCatalog.query"

export type LicenseFeatureGroupViewSwitchProps = {
  view: ComposeView
  /** The draft. */
  pickedFeatures: readonly FeatureGroupPickedLike[]
  rules: readonly FeatureGroupRuleLike[]
  onViewChange(view: ComposeView): void
}

// Three views, one draft: the sections are large enough that stacking them
// was a page of scrolling, so the operator switches between what the app
// can do, what the map offers, and the single picks. Each view's count is
// "granted of total" from its own catalog — read here from the cache the
// sections already filled.
function LicenseFeatureGroupViewSwitch(
  props: LicenseFeatureGroupViewSwitchProps,
) {
  const { view, pickedFeatures, rules, onViewChange } = props
  const { catalogCodes } = useCatalogPlacement()
  const picked = new Set(pickedFeatures.map((feature) => feature.Code))
  const isPicked = (code: string) => picked.has(code)

  const surfaceCatalog = useFeatureSurfaceCatalogQuery()
  const contentCatalog = useFeatureContentCatalogQuery()
  const surfaces =
    (surfaceCatalog.data ?? surfaceCatalog.previousData)
      ?.getFeatureSurfaceCatalog ?? []
  const contentItems =
    (contentCatalog.data ?? contentCatalog.previousData)
      ?.getFeatureContentCatalog ?? []

  const lens = useFeatureGroupLens()
  const counts: Record<ComposeView, string> = {
    capabilities: `${surfaces.filter((surface) => reachesThroughLens(lens, surface, capabilityGrantOf(surface, rules, isPicked) !== null)).length}/${surfaces.length}`,
    content: `${contentItems.filter((item) => reachesThroughLens(lens, item, grantOf(item, rules, isPicked) !== null)).length}/${contentItems.length}`,
    individual: `${individualPicksOf(pickedFeatures, catalogCodes).length}`,
  }

  return (
    <ToggleGroup
      type="single"
      size="xs"
      variant="outline"
      value={view}
      className="self-start"
      onValueChange={(next) => {
        if (next in COMPOSE_VIEW_LABELS) onViewChange(next as ComposeView)
      }}
    >
      {(Object.keys(COMPOSE_VIEW_LABELS) as ComposeView[]).map((key) => (
        <ToggleGroup.Item
          key={key}
          value={key}
          className="gap-1.5"
          data-feature-code="UI_BackofficeFeatureGroup_View_Switch"
        >
          {COMPOSE_VIEW_LABELS[key]}
          <span className="text-[11px] tabular-nums text-muted-foreground">
            {counts[key]}
          </span>
        </ToggleGroup.Item>
      ))}
    </ToggleGroup>
  )
}

export default LicenseFeatureGroupViewSwitch
